import { defaultSettings } from "../defaults";
import type { PrompterSettings } from "../types";
import { normalizeSettings } from "./session";

export const SETTINGS_KEY = "cue-studio-settings-v1";
export const APPEARANCE_KEY = "cue-studio-appearance-v2";
export const APPEARANCE_VERSION = "charcoal";
export const SPEECH_UPGRADE_KEY = "cue-studio-streaming-v1";

/** Adopt the new desk once without resetting prompt settings or future choices. */
export function readWorkspaceSettings(
  storage: Pick<Storage, "getItem" | "setItem">,
): PrompterSettings {
  let settings = { ...defaultSettings };
  try {
    settings = normalizeSettings(JSON.parse(storage.getItem(SETTINGS_KEY) || "{}"));
  } catch { /* Malformed or unavailable storage keeps usable defaults. */ }

  // Move English offline workspaces to true streaming once. Never migrate an
  // explicit provider/external choice or a non-English workspace. A later
  // choice of Whisper stays chosen. This changes no audio-upload behavior.
  try {
    if (storage.getItem(SPEECH_UPGRADE_KEY) !== "1") {
      if (settings.speechEngine === "local" && settings.language.startsWith("en"))
        settings.speechEngine = "streaming";
      storage.setItem(SETTINGS_KEY, JSON.stringify(settings));
      storage.setItem(SPEECH_UPGRADE_KEY, "1");
    }
  } catch { /* Storage health is reported by the operator desk. */ }

  try {
    if (storage.getItem(APPEARANCE_KEY) === APPEARANCE_VERSION) return settings;
  } catch { /* Storage may be unavailable in a restricted browser context. */ }

  settings.theme = "dark";
  try {
    // Commit settings before the marker so a failed write can be retried safely.
    storage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    storage.setItem(APPEARANCE_KEY, APPEARANCE_VERSION);
  } catch { /* The studio's persistence health check reports storage failures. */ }
  return settings;
}
